import React, {FC, memo, useCallback, useMemo} from "react";
import dayjs from "dayjs";
import {faCirclePause, faCirclePlay, faCircleStop} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import convertDecimalToHexColor from "../../../../../utils/convertDecimalToHexColor";
import {ActivityStatus} from "../../../../../enum/activity.enum";
import {TIMER_FORMAT} from "../../../../../constant/format";
import Timer from "../../../../Timer/Timer";
import {Activity, ActivityType} from "../../../../../model/activity.model";
import {activityStore} from "../../../../../store/activity.store";
import style from "../style.module.css";
import {getActivityStartTime} from "./helper";
import {UserSettings} from "../../../../../model/auth.model";
import getTypeFullPath from "../../../../../utils/getTypeFullPath";

interface ICurrentActivity {
  activityTypesMap: Map<string, ActivityType>
  activityTypes: ActivityType[]
  activity: Activity
  settings?: UserSettings
  inPopover?: boolean
  hideComment?: boolean
}

// Combined selector to reduce subscriptions
const useActivityActions = () => activityStore(state => ({
  stopActivityLoading: state.loading.stopActivity,
  pauseActivityLoading: state.loading.pauseActivity,
  resumeActivityLoading: state.loading.resumeActivity,
  setActivityToEdit: state.setActivityToEdit,
  stopActivity: state.stopActivity,
  pauseActivity: state.pauseActivity,
  resumeActivity: state.resumeActivity,
}));

const CurrentActivity: FC<ICurrentActivity> = memo(({ activityTypesMap, activityTypes, activity, settings, inPopover, hideComment }) => {
  const {
    stopActivityLoading,
    pauseActivityLoading,
    resumeActivityLoading,
    setActivityToEdit,
    stopActivity,
    pauseActivity,
    resumeActivity,
  } = useActivityActions();

  // O(1) lookup
  const activityType = activityTypesMap.get(activity.typeId);
  const name = settings?.fullPath ? getTypeFullPath(activityType, activityTypes) : activityType?.name;

  const handleStopActivity = useCallback(() => {
    if (!stopActivityLoading) stopActivity(activity.id);
  }, [stopActivityLoading, stopActivity, activity.id]);

  const handlePauseActivity = useCallback(() => {
    if (!pauseActivityLoading) pauseActivity(activity.id);
  }, [pauseActivityLoading, pauseActivity, activity.id]);

  const handleResumeActivity = useCallback(() => {
    if (!resumeActivityLoading) resumeActivity(activity.id);
  }, [resumeActivityLoading, resumeActivity, activity.id]);

  const setCurrentActivityToEdit = useCallback(() => {
    setActivityToEdit(activity);
  }, [setActivityToEdit, activity]);

  const timerStart = useMemo(() => getActivityStartTime(activity), [activity]);

  return (
    <div className={`${style.activity} ${inPopover ? style.inPopover : ''}`} key={activity.id}>
      <div className={style.mainRow}>
        <div
          style={{ background: activityType && convertDecimalToHexColor(activityType.color)}}
          className={style.ActivityTypesFilterLabelColor}
        />
        <span className={style.title} onClick={setCurrentActivityToEdit}>{name}</span>
        {activity.tags?.length > 0 && (
          <div className={style.tags}>
            {activity.tags.map(tag => (
              <span key={tag} className={style.tag}>{tag}</span>
            ))}
          </div>
        )}
        <span className={style.timer} onClick={setCurrentActivityToEdit}>
          {activity.status === ActivityStatus.PAUSED && dayjs.duration(activity.duration * 1000).format(TIMER_FORMAT)}
          {activity.status === ActivityStatus.RUNNING && <Timer start={timerStart}/>}
        </span>
        <div className={style.actionButtons}>
          {activity.status === ActivityStatus.RUNNING ? (
            <button
              className={style.actionButton}
              onClick={handlePauseActivity}
              disabled={pauseActivityLoading}
            >
              <FontAwesomeIcon icon={faCirclePause} />
            </button>
          ) : (
            <button
              className={style.actionButton}
              onClick={handleResumeActivity}
              disabled={resumeActivityLoading}
            >
              <FontAwesomeIcon icon={faCirclePlay} />
            </button>
          )}
          <button
            className={style.actionButton}
            onClick={handleStopActivity}
            disabled={stopActivityLoading}
          >
            <FontAwesomeIcon icon={faCircleStop} />
          </button>
        </div>
      </div>
      {activity.comment && !hideComment && (
        <div className={style.commentRow}>
          <span className={style.comment} title={activity.comment}>{activity.comment}</span>
        </div>
      )}
    </div>
  )
});

export default CurrentActivity;
